const RETURN_KEY = 'staffSignIn:returnTo';

/**
 * Starts the staff sign-in at Google with the address the dashboard already knows, skipping the identity
 * provider's own screen: someone already signed in to Google passes through without typing anything.
 */
export const staffSignInOptions = (email: string | undefined) => ({
  redirectUri: `${window.location.origin}/callback`,
  directSignIn: { method: 'social' as const, target: 'google' },
  ...(email ? { loginHint: email } : {}),
});

/** The callback lands on one fixed page; this is how it knows which page the sign-in started from. */
export const rememberStaffSignInReturn = (path: string): void => {
  try {
    sessionStorage.setItem(RETURN_KEY, path);
  } catch {
    // Without storage the visitor lands on the home page instead, which is still signed in.
  }
};

/** The page to go back to, read once. Only a path on this site, so a planted value cannot send anyone away. */
export const takeStaffSignInReturn = (): string | null => {
  try {
    const path = sessionStorage.getItem(RETURN_KEY);
    sessionStorage.removeItem(RETURN_KEY);
    return path && path.startsWith('/') && !path.startsWith('//') ? path : null;
  } catch {
    return null;
  }
};

const AUTO_KEY = 'staffSignIn:autoStarted';

/**
 * True the first time a tab asks, false after. A sign-in that comes back without a session (cancelled, or the
 * account is not in the identity provider) must stop at the button, not redirect again in a loop.
 */
export const claimAutomaticStaffSignIn = (): boolean => {
  try {
    if (sessionStorage.getItem(AUTO_KEY)) {
      return false;
    }
    sessionStorage.setItem(AUTO_KEY, '1');
    return true;
  } catch {
    return false;
  }
};

/** Once signed in, a later sign-out may start automatically again. */
export const releaseAutomaticStaffSignIn = (): void => {
  try {
    sessionStorage.removeItem(AUTO_KEY);
  } catch {
    // Nothing to release without storage.
  }
};
